import { FC, useEffect, useMemo } from 'react';

import { dayjs } from 'utils/providers/Timezone';
import { useFormikContext } from 'formik';
import DatePicker from 'react-datepicker';
import { MdNavigateBefore, MdNavigateNext } from 'react-icons/md';
import { TimeIcon } from 'ui/components/icon';
import { Dropdown } from 'ui/components/nav';
import c from 'utils/constants';
import { cn } from 'utils/helpers';

import getNextTime from '../../utils/getNextTime';
import { EVENT_FORM } from '../schema';

interface DATE_INPUT_PROPS {
  value?: Date;
  onClick?: () => void;
}

const DateInput: FC<DATE_INPUT_PROPS> = ({ onClick, value }) => (
  <button
    type="button"
    className="hover:text-primary-base text-sm"
    onClick={onClick}
  >
    {dayjs(value).format('dddd, MMM D')}
  </button>
);

interface PROPS {
  isCreated?: boolean;
}

const EventTime: FC<PROPS> = ({ isCreated }) => {
  const { values, setFieldValue } = useFormikContext<EVENT_FORM>();

  const startAtOptions = useMemo(() => {
    if (
      new Date(values.date).toDateString() !== new Date().toDateString() ||
      isCreated
    ) {
      return c.TIMES;
    }

    const tIndex = c.TIMES.findIndex((time) => time.value === getNextTime());

    return c.TIMES.filter((_, index) => index >= tIndex);
  }, [values.date, isCreated]);

  const endAtOptions = useMemo(() => {
    const tIndex = c.TIMES.findIndex((time) => time.value === values.startAt);

    return c.TIMES.filter((_, index) => index >= tIndex);
  }, [values.startAt]);

  useEffect(() => {
    // clear endAt options if exceeding the current selected
    const currentEndAt = endAtOptions.find((opt) => opt.value === values.endAt);

    if (!currentEndAt) {
      setFieldValue('endAt', null);
    }
  }, [endAtOptions, setFieldValue, values.endAt, values.startAt]);

  return (
    <>
      <div className="flex items-center py-3">
        <TimeIcon className="mr-4 h-5 w-5 text-content" />
        <div className="flex-1">
          <div className="mr-[5px] inline-block">
            <DatePicker
              selected={dayjs(values.date).toDate()}
              minDate={new Date()}
              onChange={(d) => setFieldValue('date', new Date(d ?? ''))}
              customInput={<DateInput />}
              renderCustomHeader={({
                date,
                decreaseMonth,
                increaseMonth,
                prevMonthButtonDisabled,
                nextMonthButtonDisabled,
              }) => (
                <div
                  className={cn(
                    'pb-2 px-4',
                    'flex items-center justify-between',
                    'border-b border-card-primary',
                  )}
                >
                  <p className="text-sm font-semibold">
                    {date.toLocaleString('default', {
                      month: 'long',
                    })}{' '}
                    {date.getFullYear()}
                  </p>
                  <div className="flex items-center gap-x-5">
                    <button
                      type="button"
                      onClick={decreaseMonth}
                      disabled={prevMonthButtonDisabled}
                      className="text-content"
                    >
                      <MdNavigateBefore className="text-lg" />
                    </button>
                    <button
                      type="button"
                      className={cn(
                        'text-primary-base',
                        'disabled:bg-transparent disabled:text-disabled transition',
                      )}
                      onClick={increaseMonth}
                      disabled={nextMonthButtonDisabled}
                    >
                      <MdNavigateNext className="text-lg" />
                    </button>
                  </div>
                </div>
              )}
            />
          </div>
          |
          <div className="ml-[5px] inline-flex h-5 text-blue items-center">
            <Dropdown
              placeholder="--:--"
              defaultValue={values.startAt}
              options={startAtOptions}
              onChange={(val) => setFieldValue('startAt', val.value)}
              selectedClassName={cn(
                'w-fit p-0',
                'bg-transparent justify-center h-5',
                'text-info-base text-sm',
              )}
              customIcon={<div />}
              optionsClassName="max-h-[200px] max-w-[100px]"
              className="h-5"
            />
            <span className="h-5 mx-2">-</span>
            <Dropdown
              placeholder="--:--"
              defaultValue={values.endAt}
              options={endAtOptions}
              onChange={(val) => setFieldValue('endAt', val.value)}
              selectedClassName={cn(
                'w-fit p-0',
                'bg-transparent justify-center h-5',
                'text-info-base text-sm',
              )}
              customIcon={<div />}
              optionsClassName="max-h-[200px] max-w-[100px]"
              className="h-5"
            />
          </div>
          <p className="text-content text-xs">Time zone | Does not repeat</p>
        </div>
      </div>
      {/* {errors?.date && <p className="text-danger">{errors.date}</p>} */}
    </>
  );
};

export default EventTime;
